import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Button,
  IconButton,
  Portal,
} from "@mui/material";
import { Typography, TextField, Autocomplete } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
// import SwipeableViews from 'react-swipeable-views';
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import PieChartIcon from "@mui/icons-material/PieChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import CampaignContact from "../../components/forms/CampaignContact";
import Targets from "./targets/Targets";
import Zones from "../../views/myDesign/zones/Zones";
import { useTranslation } from "react-i18next";
import Statistics from "./statistics/Statistics";
import { formatWithLocale } from "../../helpers/format";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useViewport } from "../../helpers";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import WebVR from "../../components/webVR/WebVR";
import MyDesignTargets from "../../components/createMyDesign/MyDesignTargets";
import DuplicateTarget from "../../components/createMyDesign/DuplicateTarget";
import { listTimeZones } from "timezone-support";
import { useSnackbar } from "notistack";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MyDesignCampaigns from "./campaigns/MyDesignCampaigns";
import SecurityIcon from "@mui/icons-material/Security";
import PrivacyOptions from "../../components/privacy/PrivacyOptions";
import LanguageIcon from "@mui/icons-material/Language";
import { Save } from "@mui/icons-material";
import { myDesignService } from "../../services";
import CrmContainer from "../../components/crm/Crm";
import {
  createTargets,
  duplicateTargets,
  fetchSingleMyDesign,
  fetchStats,
  fetchZones,
  updateFolderId,
} from "../../reducers/myDesigns/myDesignsThunks";
import {
  cleanCurrentDesign,
  closeBackdrop,
  duplicateCrmBackdrop,
} from "../../reducers/myDesigns/myDesignsSlice";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GppBadIcon from "@mui/icons-material/GppBad";

const PREFIX = "MyDesign";

const classes = {
  backdrop: `${PREFIX}-backdrop`,
  title: `${PREFIX}-title`,
  mainContainer: `${PREFIX}-mainContainer`,
  subheader: `${PREFIX}-subheader`,
  autocompleteContainer: `${PREFIX}-autocompleteContainer`,
  folderIdContainer: `${PREFIX}-folderIdContainer`,
  folderIdTextField: `${PREFIX}-folderIdTextField`,
  autocompleteLine: `${PREFIX}-autocompleteLine`,
  autocomplete: `${PREFIX}-autocomplete`,
  goBackButtonMargin: `${PREFIX}-goBackButtonMargin`,
  marginIcon: `${PREFIX}-marginIcon`,
  homeButton: `${PREFIX}-homeButton`,
  topMenu: `${PREFIX}-topMenu`,
  generatingContainer: `${PREFIX}-generatingContainer`,
  generatingSpinner: `${PREFIX}-generatingSpinner`,
  confidentialContainer: `${PREFIX}-confidentialContainer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  [`& .${classes.title}`]: {
    fontSize: "28px",
    marginBottom: "1rem",
    paddingTop: "1rem",
    color: "rgb(211, 175, 95)",
  },

  [`& .${classes.mainContainer}`]: {
    textAlign: "center",
    justifyContent: "space-around",
    padding: "1rem 0",
    width: "100%",
    minHeight: "100px",
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
      padding: "0",
    },
  },

  [`& .${classes.subheader}`]: {
    zIndex: 999,
    width: "100%",
    position: "sticky",
    backgroundColor: "#303030",
    top: "-10px", // hide the folder id field on scroll
  },

  [`& .${classes.autocompleteContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    margin: "1rem 0",
  },

  [`& .${classes.folderIdContainer}`]: {
    margin: "1.25rem 1.5rem 0 0",
    [theme.breakpoints.up("sm")]: {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
    },
  },

  [`& .${classes.folderIdTextField}`]: {
    marginRight: "1rem",
    width: "90px",
    [theme.breakpoints.up("sm")]: {
      width: "150px",
    },
  },

  [`& .${classes.autocompleteLine}`]: {
    margin: "1rem 0 0.25rem 0",
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.autocomplete}`]: {
    maxWidth: "350px",
    width: "100%",
    marginRight: "1rem",
  },

  [`& .${classes.goBackButtonMargin}`]: {
    margin: "1.5rem 1rem 0 1rem",
    maxHeight: "40px",
    width: "200px",
    [theme.breakpoints.between("xs", "md")]: {
      margin: "2rem 1rem 0 1rem",
    },
  },

  [`& .${classes.marginIcon}`]: {
    marginRight: "0.5rem",
  },

  [`& .${classes.homeButton}`]: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    height: "50px",
    width: "50px",
  },

  [`& .${classes.topMenu}`]: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },

  [`& .${classes.generatingContainer}`]: {
    display: "flex",
  },

  [`& .${classes.generatingSpinner}`]: {
    marginRight: "1rem",
  },

  [`& .${classes.confidentialContainer}`]: {
    display: "flex",
    "& > svg": {
      marginRight: "0.5rem",
    },
  },
}));

const ITEM_HEIGHT = 48;

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      key={index + 1}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

export default function MyDesign() {
  const { t } = useTranslation();
  const location = useLocation();
  const { width } = useViewport();
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useNavigate();
  const { id } = useParams();
  const [title, setTitle] = useState("");
  const [frameId, setFrameId] = useState(id);
  const [targetId, setTargetId] = useState(-1);
  const loading = useSelector((state) => state.myDesigns.loading);
  const [targetCreator, setTargetCreator] = useState(false);
  const [targetDuplicate, setTargetDuplicate] = useState(false);
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const isSuperAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.isSuperAdmin === 1
  );
  const [url, setUrl] = useState(null);
  const [borderlessUrl, setBorderlessUrl] = useState(null);
  const img = new Image();
  const [index, setIndex] = useState(
    location.state && location.state.index ? location.state.index : 0
  );
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const zones = useSelector((state) => state.myDesigns.currentDesign?.zones);
  const currentDesign = useSelector((state) => state.myDesigns.currentDesign);
  const updatingFolder = useSelector((state) => state.myDesigns.updateFolderId);
  const isCrmToggled = useSelector((state) => state.myDesigns?.crmEnabled);
  const [stats, setStats] = React.useState(null);
  const [height, setHeight] = React.useState(300);
  const [data, setData] = React.useState([]);
  const [resetStats, setResetStats] = useState(null);
  const zeStats = useSelector((state) => state.myDesigns.currentDesign?.stats);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [oldFolderId, setOldFolderId] = useState("");
  const [updatingConfidentiality, setUpdatingConfidentiality] = useState(false);

  const myUpdateFolderId = async () => {
    try {
      const thunk = await dispatch(
        updateFolderId({
          externalId: oldFolderId,
          frameId: currentDesign.id,
          label: currentDesign.label,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
      enqueueSnackbar(t("toasts.updateFolderIdSuccess"), {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar(t("toasts.updateFolderIdError"), { variant: "error" });
    }
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  //revoke url
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(url);
      URL.revokeObjectURL(borderlessUrl);
    };
  }, []);

  //changement de zones => stats management
  React.useMemo(() => {
    if (zones && selectedTarget) {
      const filteredZones = zones.filter(
        (z) => z.target === selectedTarget.label
      );
      let maxValue = -1;
      filteredZones.forEach((data) => {
        if (data.redirectCount > maxValue) maxValue = data.redirectCount;
      });
      const tmp = filteredZones.map((z) => {
        let opacity =
          z.redirectCount / maxValue < 0.5 ? 0.5 : z.redirectCount / maxValue;
        return {
          zone: `${z.label}`,
          activations: z.redirectCount || 0,
          activationsColor: `rgba(175, 39, 25, ${opacity})`,
        };
      });
      setData(tmp);
      setResetStats(tmp);
      setHeight(filteredZones.length * 17);
    }
  }, [zones, selectedTarget]);

  useEffect(() => {
    if (selectedTarget) setTargetId(selectedTarget.id);
  }, [selectedTarget]);

  //once stats fetched
  React.useMemo(() => {
    if (zeStats) {
      setStats(() => {
        const formatStats = zeStats.map((stat) => {
          const formatDate = formatWithLocale(
            new Date(stat.redirectDate.substring(0, 10)),
            "P"
          );
          return {
            x: formatDate,
            y: stat.redirectCount,
            date: stat.redirectDate.substring(0, 10),
          };
        });
        return [
          {
            id: "stats",
            data: formatStats,
          },
        ];
      });
    }
  }, [zeStats]);

  const getMyTarget = () => {
    if (
      currentDesign &&
      currentDesign.targets &&
      currentDesign.targets.length > 0
    ) {
      if (location.search) {
        const targetFromUrl =
          location.search.split("=").length > 0
            ? location.search.split("=")[1]
            : null;
        if (targetFromUrl) {
          const result = currentDesign.targets.find(
            (target) => target.label === targetFromUrl
          );
          if (result !== undefined) {
            setSelectedTarget(result);
          } else {
            setSelectedTarget(currentDesign.targets[0]);
          }
        }
      } else {
        if (location.state && location.state.item) {
          const target = currentDesign.targets.filter(
            (element) => location.state.item.id === element.id
          );
          if (target.length === 0) {
            setSelectedTarget(currentDesign.targets[0]);
          } else {
            setSelectedTarget(target[0]);
          }
        } else if (location.state?.hasOwnProperty("targetLabel")) {
          const oldTarget = currentDesign.targets.filter(
            (target) => location.state.targetLabel === target.label
          );
          setSelectedTarget(oldTarget[0]);
        } else {
          setSelectedTarget(currentDesign.targets[0]);
        }
      }
      setTitle(currentDesign.label);
      setUrl(currentDesign.imageUrl);
      setBorderlessUrl(currentDesign.imageBorderlessUrl);
      img.alt = "painting";
      //preload image to avoid react swipeable to lock the height
      img.onLoad = setIsImageLoaded(true);
      img.style = {
        width: "100%",
        maxWidth: "350px",
        borderRadius: "10px",
      };
      setOldFolderId(currentDesign.externalId);
    }
  };

  useEffect(() => {
    getMyTarget();
  }, [currentDesign?.id]);

  useEffect(() => {
    if (currentDesign?.imageBorderlessUrl)
      setBorderlessUrl(currentDesign.imageBorderlessUrl);
  }, [currentDesign?.imageBorderlessUrl]);

  const fetchMySingleMyDesigns = async (id) => {
    try {
      const thunk = await dispatch(fetchSingleMyDesign(id));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const fetchMyZones = async (id) => {
    try {
      const thunk = await dispatch(fetchZones(id));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const fetchMyStats = async (id) => {
    try {
      const thunk = await dispatch(fetchStats(id));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  useEffect(() => {
    if (id) {
      setSelectedTarget(null);
      getMyTarget();
      setTargetCreator(false);
      dispatch(closeBackdrop());
      fetchMySingleMyDesigns(id);
      setFrameId(id);
      fetchMyZones(id);
      fetchMyStats(id);
    }
  }, [id]);

  //update target when selected by search
  useEffect(() => {
    if (
      location.state &&
      location.state.item &&
      location.state.key &&
      currentDesign &&
      currentDesign.targets
    ) {
      const tmpTarget = currentDesign.targets.filter((target) => {
        return location.state.item.id === target.id;
      });
      setSelectedTarget(tmpTarget[0]);
    }
  }, [location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [index]);

  function handleSelectedTargetChange(value) {
    setSelectedTarget(value);
  }

  function handleChange(e, value) {
    setIndex(value);
  }

  function handleChangeIndex(index) {
    setIndex(index);
  }

  function a11yProps(index) {
    return {
      id: `scrollable-prevent-tab-${index}`,
      "aria-controls": `scrollable-prevent-tabpanel-${index}`,
    };
  }

  async function handleSubmitTargets(
    targets,
    newName = null,
    duplicate = false
  ) {
    if (duplicate) {
      const tmp = {
        name: newName,
        timezone: targets.timeZone,
        redirect: targets.targetUrl,
        blinkl: {
          providerId: targets.blinklProvider,
          campaignId: targets.blinklCampaign,
        },
      };
      try {
        const thunk = await dispatch(
          duplicateTargets({
            frameId,
            target: tmp,
            targetId: targets.id,
            cb: setSelectedTarget,
          })
        );
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
        setTargetDuplicate(false);
        enqueueSnackbar(t("toasts.createTargetsSuccess"), {
          variant: "success",
        });
      } catch {
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      }
    } else {
      targets.forEach((target) => delete target.guid);
      try {
        const thunk = await dispatch(
          createTargets({ frameId, targets, cb: setSelectedTarget })
        );
        if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
        setTargetCreator(false);
        enqueueSnackbar(t("toasts.createTargetsSuccess"), {
          variant: "success",
        });
      } catch {
        enqueueSnackbar(t("toasts.createTargetsError"), { variant: "error" });
      }
    }
  }

  const crmToggle = useSelector((state) => state.myDesigns?.crmEnabled);

  const options = useMemo(() => {
    const accessOptions = [
      { key: "add", label: "Add" },
      { key: "duplicate", label: "Duplicate" },
    ];
    return !crmToggle
      ? [
          ...accessOptions,
          { key: "confidentiality", label: "confidentiality" },
          { key: "scan", label: "Scan" },
        ]
      : accessOptions;
  }, [crmToggle]);

  const getViews = () => {
    const views = [
      <TabPanel value={index} index={0} dir={theme.direction} key="panel0">
        <Root>
          <Container className={classes.mainContainer}>
            <Targets
              frameId={frameId}
              frameName={title}
              url={url}
              setUrl={setUrl}
              borderlessUrl={borderlessUrl}
              setBorderlessUrl={setBorderlessUrl}
              targets={currentDesign.targets}
              selectedTarget={selectedTarget}
              setSelectedTarget={setSelectedTarget}
              currentDesign={currentDesign}
            />
          </Container>
        </Root>
      </TabPanel>,
    ];
    if (isAdmin) {
      views.push(
        <TabPanel value={index} index={1} dir={theme.direction} key="panel1">
          <Portal>
            <WebVR
              mode="frame"
              frameId={frameId}
              currentDesign={currentDesign}
              selectedTarget={selectedTarget}
              setSelectedTarget={setSelectedTarget}
              borderlessUrl={borderlessUrl}
            />
          </Portal>
        </TabPanel>,
        <TabPanel value={index} index={2} dir={theme.direction} key="panel2">
          <Zones
            zones={currentDesign.zones}
            frameId={frameId}
            selectedTarget={selectedTarget}
            frameName={title}
            isAdmin={isAdmin}
            parentUrl={url}
            targetId={targetId}
          />
        </TabPanel>,
        <TabPanel value={index} index={3} dir={theme.direction} key="panel3">
          <Statistics
            data={data}
            setData={setData}
            height={height}
            selectedTarget={selectedTarget}
            zones={currentDesign.zones}
            stats={stats}
            setStats={setStats}
            resetStats={resetStats}
            isAdmin={isAdmin}
            fetchStats={() => {
              fetchMyZones(id);
            }}
          />
        </TabPanel>,
        <TabPanel value={index} index={4} dir={theme.direction} key="panel4">
          <MyDesignCampaigns
            frameId={frameId}
            selectedTarget={selectedTarget}
          />
        </TabPanel>,
        <TabPanel value={index} index={5} dir={theme.direction} key="panel5">
          <PrivacyOptions
            frameId={frameId}
            geolocalisation={false}
            positions={[]}
          />
        </TabPanel>
      );
    } else {
      views.push(
        <TabPanel value={index} index={1} dir={theme.direction} key="panel6">
          <Zones
            zones={currentDesign.zones}
            frameId={frameId}
            selectedTarget={selectedTarget}
            frameName={title}
            parentUrl={url}
          />
        </TabPanel>,
        <TabPanel value={index} index={2} dir={theme.direction} key="panel7">
          <Statistics
            data={data}
            setData={setData}
            height={height}
            selectedTarget={selectedTarget}
            zones={currentDesign.zones}
            stats={stats}
            setStats={setStats}
            resetStats={resetStats}
            isAdmin={isAdmin}
            fetchStats={() => {
              fetchMyZones(id);
            }}
          />
        </TabPanel>,
        <TabPanel value={index} index={3} dir={theme.direction} key="panel8">
          <CampaignContact frameId={frameId} />
        </TabPanel>
      );
    }
    return views;
  };

  return (
    <Root>
      {isAdmin && (
        <React.Fragment>
          <ResponsiveDialogManager
            open={targetCreator}
            disableGutters
            fullScreen
            renderer={
              <MyDesignTargets
                frameId={frameId}
                setSelectedTarget={setSelectedTarget}
                listTimeZones={listTimeZones}
                loading={loading}
                handleSubmit={handleSubmitTargets}
              />
            }
            title={t("homePage.myDesign.createForm.createFormMod")}
            onClose={() => setTargetCreator(false)}
          />
          <ResponsiveDialogManager
            open={targetDuplicate}
            disableGutters
            fullScreen
            renderer={
              <DuplicateTarget
                frameId={frameId}
                setSelectedTarget={setSelectedTarget}
                listTimeZones={listTimeZones}
                loading={loading}
                handleSubmit={handleSubmitTargets}
                targetDuplicate
                selectedTarget={selectedTarget}
              />
            }
            title={`${t("homePage.myDesign.createForm.duplicateTarget")}: ${
              selectedTarget && selectedTarget.label
            } `}
            onClose={() => setTargetDuplicate(false)}
          />
        </React.Fragment>
      )}
      <div className={classes.topMenu}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.goBackButtonMargin}
          onClick={() => {
            dispatch(cleanCurrentDesign());
            history(`/mydesigns`);
          }}
        >
          <KeyboardReturnIcon className={classes.marginIcon} />
          {t("topMenu.myDesigns")}
        </Button>
        {currentDesign && currentDesign.targets && !loading && (
          <div className={classes.folderIdContainer}>
            <TextField
              label={t("myDesign.folderId")}
              variant="outlined"
              value={oldFolderId}
              disabled={!isAdmin || updatingFolder}
              className={classes.folderIdTextField}
              onChange={(e) => setOldFolderId(e.target.value)}
            />
            {oldFolderId !== currentDesign.externalId && (
              <IconButton
                color="primary"
                className={classes.homeButton}
                onClick={(e) => {
                  myUpdateFolderId();
                }}
                size="large"
              >
                {updatingFolder ? (
                  <CircularProgress size={28} style={{ color: "#000" }} />
                ) : (
                  <Save style={{ color: "#000" }} />
                )}
              </IconButton>
            )}
          </div>
        )}
      </div>
      <div className={classes.subheader}>
        <Container style={{ textAlign: "center" }}>
          {currentDesign && currentDesign.targets && !loading ? (
            <div className={classes.autocompleteContainer}>
              <div className={classes.autocompleteLine}>
                <Autocomplete
                  label="targets"
                  id="autocomplete-targets"
                  disableClearable
                  className={classes.autocomplete}
                  options={
                    currentDesign &&
                    currentDesign.targets.map((target) => target)
                  }
                  getOptionLabel={(option) =>
                    option.label !== undefined && option.label !== ""
                      ? title + " (" + option.id + ") - " + option.label
                      : ""
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("myDesign.targetAutocomplete")}
                      variant="outlined"
                    />
                  )}
                  value={selectedTarget}
                  onChange={(e, value) => handleSelectedTargetChange(value)}
                />
                {isAdmin && (
                  <React.Fragment>
                    <IconButton
                      aria-label="more"
                      aria-controls="long-menu"
                      aria-haspopup="true"
                      color="primary"
                      component="span"
                      className={classes.homeButton}
                      onClick={handleOpenMenu}
                      size="large"
                    >
                      <MoreVertIcon style={{ color: "#000" }} />
                    </IconButton>
                    <Menu
                      id="long-menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={open}
                      onClose={handleCloseMenu}
                      slotProps={{
                        paper: {
                          style: {
                            maxHeight: ITEM_HEIGHT * 5.5,
                            width: isAdmin ? "26ch" : "20ch",
                            marginTop: "-50px", // button size
                          },
                        },
                      }}
                    >
                      {options.map((option, index) => {
                        if (option.key === "confidentiality") {
                          if (isSuperAdmin) {
                            return (
                              <MenuItem
                                key={`${option}-${index + 1}-confidentiality`}
                                onClick={() => {
                                  setUpdatingConfidentiality(true);
                                  myDesignService
                                    .updateConfidential(
                                      currentDesign.id,
                                      !currentDesign.confidentiality
                                    )
                                    .then(() => {
                                      fetchMySingleMyDesigns(currentDesign.id);
                                      enqueueSnackbar(
                                        t(
                                          "toasts.updateConfidentialitySuccess"
                                        ),
                                        { variant: "success" }
                                      );
                                    })
                                    .catch((err) => {
                                      if (err === 403)
                                        enqueueSnackbar(
                                          t("toasts.unauthorizedError"),
                                          {
                                            variant: "error",
                                          }
                                        );
                                      else
                                        enqueueSnackbar(
                                          t(
                                            "toasts.updateConfidentialityError"
                                          ),
                                          { variant: "error" }
                                        );
                                    })
                                    .finally(() => {
                                      setUpdatingConfidentiality(false);
                                      handleCloseMenu();
                                    });
                                }}
                              >
                                <Root>
                                  {updatingConfidentiality ? (
                                    <div
                                      className={classes.generatingContainer}
                                    >
                                      <CircularProgress
                                        size={28}
                                        className={classes.generatingSpinner}
                                      />
                                      <span>
                                        {t(
                                          "myDesign.zones.actionsMenu.updatingConfidential"
                                        )}
                                      </span>
                                    </div>
                                  ) : (
                                    <div
                                      className={classes.confidentialContainer}
                                    >
                                      {currentDesign.confidentiality ? (
                                        <VerifiedUserIcon />
                                      ) : (
                                        <GppBadIcon />
                                      )}
                                      {currentDesign.confidentiality
                                        ? t(
                                            "myDesign.zones.actionsMenu.confidential"
                                          )
                                        : t(
                                            "myDesign.zones.actionsMenu.notConfidential"
                                          )}
                                    </div>
                                  )}
                                </Root>
                              </MenuItem>
                            );
                          } else return null;
                        } else {
                          return (
                            <MenuItem
                              key={`${option}-${index + 1}`}
                              onClick={() => {
                                if (option.key === "add") {
                                  setTargetCreator(true);
                                  handleCloseMenu();
                                } else if (option.key === "scan") {
                                  window.location.href = `https://scan.fanart.zone?target=${selectedTarget.id}`;
                                } else {
                                  if (crmToggle) {
                                    // toggle une variable dans redux pour trigger coté crm
                                    dispatch(duplicateCrmBackdrop());
                                  } else setTargetDuplicate(true);
                                  handleCloseMenu();
                                }
                              }}
                            >
                              {option.key === "add"
                                ? t("myDesign.informations.add")
                                : option.key === "duplicate"
                                ? t("myDesign.informations.duplicate")
                                : option.key === "scan"
                                ? t("myDesign.informations.scanPage")
                                : ""}
                            </MenuItem>
                          );
                        }
                      })}
                    </Menu>
                  </React.Fragment>
                )}
              </div>
            </div>
          ) : (
            <div />
          )}
        </Container>
        {!isCrmToggled && (
          <Tabs
            value={index}
            variant={width <= 520 ? "scrollable" : "fullWidth"}
            onChange={handleChange}
            indicatorColor="primary"
            scrollButtons="auto"
            aria-label="full width tabs my design"
          >
            <Tab
              icon={<TrackChangesIcon />}
              aria-label="informations"
              {...a11yProps(0)}
            />
            {isAdmin && (
              <Tab
                icon={<LanguageIcon />}
                aria-label="web"
                {...a11yProps(1)}
                disabled={!selectedTarget}
              />
            )}
            <Tab
              icon={<PieChartIcon />}
              aria-label="targets"
              {...a11yProps(isAdmin ? 2 : 1)}
              disabled={!selectedTarget}
            />
            <Tab
              icon={<BarChartIcon />}
              aria-label="campaign"
              {...a11yProps(isAdmin ? 3 : 2)}
              disabled={!selectedTarget}
            />
            <Tab
              icon={<GroupWorkIcon />}
              aria-label="videos"
              {...a11yProps(isAdmin ? 4 : 3)}
              disabled={isAdmin && !selectedTarget}
            />
            {isAdmin && (
              <Tab
                icon={<SecurityIcon />}
                aria-label="privacy"
                {...a11yProps(isAdmin ? 5 : 4)}
              />
            )}
          </Tabs>
        )}
      </div>
      {!isCrmToggled ? (
        <>
          {currentDesign ? (
            // <SwipeableViews
            //   axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
            //   index={index}
            //   onChangeIndex={handleChangeIndex}
            // >
            <>{getViews()}</>
          ) : (
            // {/* </SwipeableViews> */}
            <Container>
              <div className="loading-container">
                <CircularProgress size={50} />
                <Typography>{t("loaders.fetching")}</Typography>
              </div>
            </Container>
          )}
        </>
      ) : (
        <CrmContainer
          selectedTarget={selectedTarget}
          setSelectedTarget={setSelectedTarget}
          imgUrl={borderlessUrl || currentDesign?.imageUrl || null}
        />
      )}
    </Root>
  );
}
